import useAlert from '../../../components/Alert/hooks/useAlert'
import { updateOrgUserEmail } from '../repository/updateOrgUserEmail'
import { updatePartnerGeneralInfo } from '../repository/updatePartnerGeneralInfo'
import Storage from '../../../lib/Storage'
import { updateOrgUserPassword } from '../repository/updateOrgUserPassword'
import { PartnerGeneralInfo } from '../types/PartnerGeneralInfo'
import { updateVendorGeneralInfo } from '../repository/updateVendorGeneralInfo'
import { AddressData } from '../repository/getAddresses'
import { updateVendorAddressInfo } from '../repository/updateVendorAddressInfo'

type PasswordUpdate = {
  password: string
  confirmPassword: string
}

const validateInfo = (data: PartnerGeneralInfo) => {
  const errors = []

  if (!data.primaryContact) errors.push('Primary contact is required')
  if (!data.phoneNumber) errors.push('Phone number is required')

  return errors
}

const validateEmail = (email: string) => {
  const errors = []

  if (!email) errors.push('Email is required')

  return errors
}

const validatePassword = (data: PasswordUpdate) => {
  const errors = []

  if (!data.password) errors.push('Password is required')
  if (!data.confirmPassword) errors.push('Confirm password is required')
  if (data.password !== data.confirmPassword)
    errors.push('Passwords do not match')
  if (data.password.length < 6)
    errors.push('Password must be at least 6 characters long')

  return errors
}

const validateVendorInfo = (data: PartnerGeneralInfo) => {
  const errors = []

  if (!data.primaryContact) errors.push('Primary contact is required')
  if (!data.phoneNumber) errors.push('Phone number is required')
  if (!data.supportContactStackCommerce)
    errors.push('Support contact (StackCommerce) is required')
  if (!data.fulfillmentContact) errors.push('Fulfillment contact is required')

  return errors
}

const validateVendorAddress = (data: AddressData) => {
  const errors = []
  if (!data.address_1) errors.push('Address is required')
  if (!data.city) errors.push('City is required')
  if (!data.zip) errors.push('Zip code is required')
  if (!data.country) errors.push('Country is required')

  return errors
}

const setUserEmail = (email: string) => {
  const user = Storage().getObject('user')
  Storage().setObject('user', { ...user, email })
}

const useAccount = () => {
  const { alertSuccess, alertErrors } = useAlert()

  const validate = <T>(
    data: T,
    validate: (data: T) => string[],
    onValidate: (data: T) => void
  ) => {
    const errors = validate(data)

    if (errors.length > 0) {
      alertErrors(errors)
    } else {
      onValidate(data)
    }
  }

  const updateInfo = (info: PartnerGeneralInfo) => {
    validate(info, validateInfo, (info) => {
      updatePartnerGeneralInfo(info).then((data) => {
        if (!data.error) {
          alertSuccess('Info successfully updated!')
        } else alertErrors(['Something went wrong. Please try again.'])
      })
    })
  }

  const updateEmail = (email: string) => {
    validate(email, validateEmail, (email) => {
      updateOrgUserEmail(email).then((data) => {
        if (!data.error) {
          const email = data.data.attributes.email
          setUserEmail(email)
          alertSuccess('Email successfully updated!')
        } else alertErrors(['Something went wrong. Please try again'])
      })
    })
  }

  const updatePassword = (passwords: PasswordUpdate) => {
    validate(passwords, validatePassword, (passwords) => {
      const { password } = passwords
      updateOrgUserPassword(password).then((data) => {
        if (data.error) {
          alertErrors(['Something went wrong. Please try again'])
        } else {
          alertSuccess('Password successfully updated!')
        }
      })
    })
  }

  const updateVendorInfo = (info: PartnerGeneralInfo) => {
    validate(info, validateVendorInfo, (info) => {
      updateVendorGeneralInfo(info).then((data) => {
        if (!data.error) {
          alertSuccess('Info successfully updated!')
        } else alertErrors(['Something went wrong. Please try again.'])
      })
    })
  }

  const updateVendorAddress = (
    info: AddressData,
    verificationState: string
  ) => {
    validate(info, validateVendorAddress, (info) => {
      updateVendorAddressInfo(info, verificationState).then((data) => {
        if (!data.error) {
          alertSuccess('Info successfully updated!')
        } else alertErrors(['Something went wrong. Please try again.'])
      })
    })
  }

  return {
    updateInfo,
    updateEmail,
    updatePassword,
    updateVendorInfo,
    updateVendorAddress
  }
}

export default useAccount
